/**
 * Discover Workspaces (Figma 10216:3878): every workspace in the caller's
 * organization (same email domain) plus their own, as a 3-column card grid or
 * a row list.  Search, filter by user / owner / status, sort, open a
 * workspace's members, and ask to join (or cancel the request) from the card.
 */
import React, { useMemo, useState } from 'react';
import { DropdownMenu, EmptyState, Skeleton, SparkIcon, Text } from '@spark';
import useDiscoverWorkspaces from '../../hooks/useDiscoverWorkspaces';
import {
    DEFAULT_SORT,
    EMPTY_FILTERS,
    SORT_OPTIONS,
    filterWorkspaces,
    ownerOptions,
    peopleOptions,
    sortWorkspaces,
} from '../../utils/discoverFilters';
import DiscoverToolbar from './DiscoverToolbar';
import WorkspaceDiscoverCard from './WorkspaceDiscoverCard';
import WorkspaceMembersModal from './WorkspaceMembersModal';
import './discover.css';

export const PAGE_TITLE = 'Discover Workspaces';
export const PAGE_SUBTITLE = 'Find and request access to workspaces across your organization';
export const sectionTitle = (count) => `Organization Workspaces (${count})`;

const SKELETON_CARDS = 6;

function CardSkeletons({ layout }) {
    return (
        <div className={`discover-grid discover-grid--${layout}`} aria-busy="true" data-testid="discover-loading">
            {Array.from({ length: SKELETON_CARDS }, (_, index) => (
                <div key={index} className={`discover-card discover-card--${layout} discover-card--skeleton`}>
                    <div className="discover-card__identity">
                        <Skeleton shapeType="circle" width={60} height={60} />
                        <div className="discover-card__title">
                            <Skeleton width={160} height={16} />
                            <Skeleton width={120} height={12} />
                        </div>
                    </div>
                    <div className="discover-card__slot" />
                    <div className="discover-card__members">
                        <Skeleton width={60} height={12} />
                        <Skeleton width={220} height={32} />
                    </div>
                </div>
            ))}
        </div>
    );
}

export function DiscoverWorkspacesPage() {
    const { workspaces, people, loading, error, busyIds, reload, askToJoin, cancelRequest } = useDiscoverWorkspaces();
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [view, setView] = useState('grid');
    const [membersOf, setMembersOf] = useState(null);

    const visible = useMemo(() => sortWorkspaces(filterWorkspaces(workspaces, filters), sort), [workspaces, filters, sort]);
    const userChoices = useMemo(() => peopleOptions(workspaces, people), [workspaces, people]);
    const ownerChoices = useMemo(() => ownerOptions(workspaces, people), [workspaces, people]);
    const highlightEmail = filters.userEmail || filters.ownerEmail;
    const sortLabel = SORT_OPTIONS.find((option) => option.id === sort)?.label || SORT_OPTIONS[0].label;

    let content;
    if (loading) {
        content = <CardSkeletons layout={view} />;
    } else if (error) {
        content = (
            <EmptyState
                className="discover-empty"
                size="medium"
                illustration="illustration-feedback-face-sad-outline"
                title="Couldn't load workspaces"
                subtitle={error}
                showMainButton
                ctaLabel="Try again"
                showCtaIcon={false}
                onCtaClick={() => reload()}
            />
        );
    } else if (workspaces.length === 0) {
        content = (
            <EmptyState
                className="discover-empty"
                size="medium"
                illustration="illustration-box-open"
                title="No workspaces to discover yet"
                subtitle="Workspaces created by people at your organization will show up here."
                showMainButton={false}
            />
        );
    } else if (visible.length === 0) {
        content = (
            <EmptyState
                className="discover-empty"
                size="medium"
                illustration="illustration-no-results"
                title="No workspaces match your filters"
                subtitle="Try a different name, person or status."
                showMainButton
                ctaLabel="Clear filters"
                showCtaIcon={false}
                onCtaClick={() => setFilters(EMPTY_FILTERS)}
            />
        );
    } else {
        content = (
            <div className={`discover-grid discover-grid--${view}`} data-testid="discover-grid" data-view={view}>
                {visible.map((workspace) => (
                    <WorkspaceDiscoverCard
                        key={workspace.id}
                        workspace={workspace}
                        people={people}
                        highlightEmail={highlightEmail}
                        layout={view}
                        busy={busyIds.has(workspace.id)}
                        onOpenMembers={setMembersOf}
                        onAskToJoin={askToJoin}
                        onCancelRequest={cancelRequest}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className="discover-page" data-testid="discover-workspaces-page">
            <header className="discover-page__header">
                <h1 className="discover-page__title fs-unmask">{PAGE_TITLE}</h1>
                <Text as="p" variant="base-medium" color="secondary" className="discover-page__subtitle">
                    {PAGE_SUBTITLE}
                </Text>
                <DiscoverToolbar
                    filters={filters}
                    onFiltersChange={setFilters}
                    peopleOptions={userChoices}
                    ownerOptions={ownerChoices}
                    view={view}
                    onViewChange={setView}
                />
            </header>
            <section className="discover-page__section" aria-label="Organization workspaces">
                <div className="discover-page__section-head">
                    <Text as="h2" variant="heading-5-semibold" className="discover-page__section-title" data-testid="discover-section-title">
                        {sectionTitle(loading ? 0 : visible.length)}
                    </Text>
                    <div className="discover-page__sort" data-testid="discover-sort">
                        <DropdownMenu
                            fill
                            showLabel={false}
                            placeholder={SORT_OPTIONS[0].label}
                            prefixIcon={<SparkIcon slug="filter" size="small" />}
                            items={SORT_OPTIONS.map(({ label }) => ({ label }))}
                            value={sortLabel}
                            onSelect={(item) => {
                                const option = SORT_OPTIONS.find((candidate) => candidate.label === item.label);
                                setSort(option?.id || DEFAULT_SORT);
                            }}
                        />
                    </div>
                </div>
                {content}
            </section>
            <WorkspaceMembersModal open={Boolean(membersOf)} workspace={membersOf} onClose={() => setMembersOf(null)} />
        </div>
    );
}

export default DiscoverWorkspacesPage;
